import {
  Children,
  EntityIDMap,
  EntityPermissions,
  Permission,
  PhoneType,
  Tenant,
  TenantGroup,
  User,
  defaultEntityPermissions,
  superAdminPermissions,
  Violation,
  FileLink,
  SystemData,
} from "types";
import { ROUTES, commonRoutes, privateRoutes, superAdminRoutes } from "router";
import { ReactNode, createContext, useCallback, useReducer } from "react";
import { deleteCookie, setCookieValue } from "utils";
import { capitalize } from "@mui/material";
import AuthService from "utils/AuthService";

interface FeatureSwitches {
  permitService: boolean;
  allowJobCancellation: boolean;
}

interface AppState {
  user: User; // active user
  headerText: string; // text to display in the header
  entityName: string; // entity name
  tenant: Tenant; // currently selected tenant
  routes: Array<ROUTES>; // all available routes
  userTenants: Array<Tenant>; // tenants the user is authorized to access
  phoneTypes: Array<PhoneType>;
  entityPermissions: EntityPermissions; // permissions for the user
  standardViolations: Array<Violation>; // standard violations
  colors: Array<string>; // colors for the cars
  features: FeatureSwitches;
  signature: FileLink | null;
}
interface SET_USER_ACTION {
  type: ACTION_TYPE.SET_USER;
  payload: User;
}

interface SET_HEADER_TEXT {
  type: ACTION_TYPE.SET_HEADER_TEXT;
  payload: string;
}

interface SET_ENTITY_NAME {
  type: ACTION_TYPE.SET_ENTITY_NAME;
  payload: string;
}
interface SET_ACTIVE_TENANT_ACTION {
  type: ACTION_TYPE.SET_ACTIVE_TENANT;
  payload: Tenant;
}
interface SET_USER_TENANTS_ACTION {
  type: ACTION_TYPE.SET_USER_TENANTS;
  payload: Array<Tenant>;
}
interface SET_PHONE_TYPES_ACTION {
  type: ACTION_TYPE.SET_PHONE_TYPES;
  payload: Array<PhoneType>;
}
interface SET_STANDARD_VIOLATIONS_ACTION {
  type: ACTION_TYPE.SET_STANDARD_VIOLATIONS;
  payload: Array<Violation>;
}
interface ADD_USER_TENANT_ACTION {
  type: ACTION_TYPE.ADD_USER_TENANT;
  payload: Tenant;
}
interface SET_COLORS_ACTION {
  type: ACTION_TYPE.SET_COLORS;
  payload: Array<string>;
}

interface SET_ENTITY_PERMISSIONS {
  type: ACTION_TYPE.SET_ENTITY_PERMISSIONS;
  payload: EntityPermissions;
}
interface UPDATE_CURRENT_TENANT_GROUP {
  type: ACTION_TYPE.UPDATE_CURRENT_TENANT_GROUP;
  payload: TenantGroup;
}
interface LOGOUT {
  type: ACTION_TYPE.LOGOUT;
}

interface SET_SIGNATURE {
  type: ACTION_TYPE.SET_SIGNATURE;
  payload: FileLink;
}

interface SET_FEATURES {
  type: ACTION_TYPE.SET_FEATURES;
  payload: FeatureSwitches;
}

export const enum ACTION_TYPE {
  SET_USER = "SET_USER",
  SET_HEADER_TEXT = "SET_HEADER_TEXT",
  SET_ENTITY_NAME = "SET_ENTITY_NAME",
  SET_TENANT_ID = "SET_TENANT_ID",
  SET_TENANT_TIMEZONE = "SET_TENANT_TIME_ZONE",
  SET_USER_TENANTS = "SET_USER_TENANTS",
  SET_PHONE_TYPES = "SET_PHONE_TYPES",
  SET_STANDARD_VIOLATIONS = "SET_STANDARD_VIOLATIONS",
  ADD_USER_TENANT = "ADD_USER_TENANT",
  SET_COLORS = "SET_COLORS",
  SET_ACTIVE_TENANT = "SET_ACTIVE_TENANT",
  SET_ENTITY_PERMISSIONS = "SET_ENTITY_PERMISSIONS",
  UPDATE_CURRENT_TENANT_GROUP = "UPDATE_CURRENT_TENANT_GROUP",
  LOGOUT = "LOGOUT",
  SET_SIGNATURE = "SET_SIGNATURE",
  SET_FEATURES = "SET_FEATURES",
}
type Action =
  | SET_USER_ACTION
  | SET_HEADER_TEXT
  | SET_ENTITY_NAME
  | SET_ACTIVE_TENANT_ACTION
  | SET_USER_TENANTS_ACTION
  | SET_PHONE_TYPES_ACTION
  | SET_STANDARD_VIOLATIONS_ACTION
  | ADD_USER_TENANT_ACTION
  | SET_COLORS_ACTION
  | SET_ENTITY_PERMISSIONS
  | UPDATE_CURRENT_TENANT_GROUP
  | LOGOUT
  | SET_SIGNATURE
  | SET_FEATURES;

const defaultState: AppState = {
  user: {} as User,
  headerText: "Welcome!",
  entityName: "",
  tenant: {} as Tenant,
  routes: [],
  userTenants: [],
  entityPermissions: defaultEntityPermissions,
  phoneTypes: [],
  standardViolations: [],
  colors: [],
  features: {
    permitService: false,
    allowJobCancellation: false,
  },
  signature: null,
};

function getInitialState(): AppState {
  const stateString = sessionStorage.getItem("state");
  if (stateString) {
    return JSON.parse(stateString);
  } else {
    return defaultState;
  }
}

function getAuthorizedRoutesForUser(
  user: User,
  currentTenantID: number,
  features: FeatureSwitches,
): Array<ROUTES> {
  if (AuthService.IsSuperAdmin()) {
    return [...superAdminRoutes, ...privateRoutes];
  } else {
    let routesArray: Array<ROUTES> = [];

    user.roles
      .filter((r) => r.tenantID === currentTenantID)
      .forEach((role) => {
        role.permissions?.forEach((p: Permission) => {
          if (p.create || p.read || p.update) {
            const routes = getRoutesForPermission(p, features, role.id);
            routesArray = [...routesArray, ...routes];
          }
        });
      });
    routesArray = [...routesArray, ...commonRoutes];
    const routeSet = new Set(routesArray);
    return Array.from(routeSet);
  }
}

const getRoutesForPermission = (
  p: Permission,
  features: FeatureSwitches,
  roleID: number,
): Array<ROUTES> => {
  const routes: Array<ROUTES> = [];
  const isPM = roleID === 5;
  switch (p.entity.id as EntityIDMap) {
    case EntityIDMap.Tenant:
      routes.push(ROUTES.TENANTS);
      routes.push(ROUTES.TENANTS_FORM);
      routes.push(ROUTES.COMPANY_SETUP);
      break;
    case EntityIDMap.Contact:
      if (!isPM) {
        routes.push(ROUTES.CONTACTS);
        routes.push(ROUTES.CONTACTS_FORM);
        routes.push(ROUTES.PEOPLE);
      }
      routes.push(ROUTES.PROPERTY_CONTACTS);
      routes.push(ROUTES.PROPERTY_CONTACTS_FORM);
      routes.push(ROUTES.PROPERTY_GROUP_CONTACTS);
      routes.push(ROUTES.PROPERTY_GROUP_CONTACTS_FORM);
      routes.push(ROUTES.PROPERTY_GROUP_PEOPLE);
      routes.push(ROUTES.PROPERTY_PEOPLE);
      
      break;
    case EntityIDMap.Job:
      routes.push(ROUTES.JOBS_DETAILS);
      routes.push(ROUTES.JOBS);
      routes.push(ROUTES.ACTIVITY_FEED);
      routes.push(ROUTES.PROPERTY_JOBS);
      routes.push(ROUTES.PROPERTY_GROUP_JOBS);
      routes.push(ROUTES.PROPERTY_JOBS_DETAILS);
      routes.push(ROUTES.PROPERTY_GROUP_JOBS_DETAILS);
      if (p.create) {
        routes.push(ROUTES.PROPERTY_JOBS_CREATE);
        routes.push(ROUTES.PROPERTY_GROUP_JOBS_CREATE);
        routes.push(ROUTES.JOBS_CREATE);
      }
      break;
    case EntityIDMap.Rule:
      if (!isPM) {
        routes.push(ROUTES.RULES_CONFIG);
        routes.push(ROUTES.RULES);
        routes.push(ROUTES.RULES_FORM);
        routes.push(ROUTES.VIOLATIONS);
        routes.push(ROUTES.RULESETS);
        routes.push(ROUTES.RULESETS_FORM);
      }
      routes.push(ROUTES.PROPERTY_RULES_CONFIG);
      routes.push(ROUTES.PROPERTY_GROUP_RULES_CONFIG);
      routes.push(ROUTES.PROPERTY_RULES);
      routes.push(ROUTES.PROPERTY_RULES_FORM);
      routes.push(ROUTES.PROPERTY_RULESETS);
      routes.push(ROUTES.PROPERTY_RULESETS_FORM);
      routes.push(ROUTES.PROPERTY_DO_NOT_TOW);
      routes.push(ROUTES.PROPERTY_DO_NOT_TOW_FORM);
      routes.push(ROUTES.PROPERTY_GROUP_DO_NOT_TOW_FORM);
      routes.push(ROUTES.PROPERTY_GROUP_RULES);
      routes.push(ROUTES.PROPERTY_GROUP_RULES_FORM);
      routes.push(ROUTES.PROPERTY_GROUP_RULESETS);
      routes.push(ROUTES.PROPERTY_GROUP_RULESETS_FORM);
      routes.push(ROUTES.PROPERTY_GROUP_DO_NOT_TOW);
      break;
    case EntityIDMap.Property:
      routes.push(ROUTES.PROPERTY);
      routes.push(ROUTES.PROPERTIES);
      routes.push(ROUTES.PROPERTY_BRANDING);
      routes.push(ROUTES.PROPERTY_GROUP_PROPERTIES);
      routes.push(ROUTES.PROPERTY_FILES);
      routes.push(ROUTES.PROPERTY_QR_CODES);
      routes.push(ROUTES.PROPERTY_QR_CODES_FORM);
      if (p.create) {
        routes.push(ROUTES.PROPERTY_CREATE);
        routes.push(ROUTES.PROPERTY_GROUP_PROPERTIES_CREATE);
      }
      break;
    case EntityIDMap.Property_Group:
      routes.push(ROUTES.PROPERTY_GROUP);
      routes.push(ROUTES.PROPERTY_GROUPS);
      routes.push(ROUTES.PROPERTY_GROUP_BRANDING);
      routes.push(ROUTES.PROPERTY_GROUP_QR_CODES);
      routes.push(ROUTES.PROPERTY_GROUP_QR_CODES_FORM);
      routes.push(ROUTES.PROPERTY_GROUP_FILES);
      routes.push(ROUTES.PROPERTY_GROUP_PROPERTIES);
      if (p.create) {
        routes.push(ROUTES.PROPERTY_GROUP_CREATE);
      }
      break;
    case EntityIDMap.User:
      if (!isPM) {
        routes.push(ROUTES.USERS);
        routes.push(ROUTES.USERS_FORM);
        routes.push(ROUTES.PEOPLE);
      }
      routes.push(ROUTES.PROPERTY_USERS);
      routes.push(ROUTES.PROPERTY_USERS_FORM);
      routes.push(ROUTES.PROPERTY_GROUP_USERS);
      routes.push(ROUTES.PROPERTY_GROUP_USERS_FORM);
      routes.push(ROUTES.PROPERTY_GROUP_PEOPLE);
      routes.push(ROUTES.PROPERTY_PEOPLE);
      break;
    case EntityIDMap.Permit:
      if (features.permitService) {
        routes.push(ROUTES.PROPERTY_PERMITS);
        routes.push(ROUTES.PROPERTY_PERMIT_DETAILS);
        routes.push(ROUTES.PROPERTY_PERMITS_IMPORT);
        routes.push(ROUTES.PROPERTY_PERMIT_TYPES);
        routes.push(ROUTES.PROPERTY_PERMIT_TYPES_FORM);
        routes.push(ROUTES.PROPERTY_GROUP_PERMIT_TYPES);
        routes.push(ROUTES.PROPERTY_GROUP_PERMIT_TYPES_FORM);
      }
      break;
    case EntityIDMap.Permission:
      if (!isPM) {
        routes.push(ROUTES.PERMISSIONS);
      }
      break;
  }
  return routes;
};

const getEntityPermissions = (
  user: User,
  tenantID: number,
): EntityPermissions => {
  // super admins don't have role permissions
  if (AuthService.IsSuperAdmin()) {
    return superAdminPermissions;
  }

  const entityPermissions: EntityPermissions = { ...defaultEntityPermissions };
  user.roles
    .filter((role) => role.tenantID === tenantID)
    .forEach((role) => {
      role.permissions?.forEach((permission) => {
        if (permission.create || permission.read || permission.update) {
          const permissionKey = EntityIDMap[permission.entity.id].toLowerCase();
          if (permissionKey) {
            entityPermissions[permissionKey as keyof EntityPermissions] = {
              //don't overwrite if already true (any scope is enough)
              create: entityPermissions[
                permissionKey as keyof EntityPermissions
              ]?.create
                ? true
                : permission.create,
              read: entityPermissions[permissionKey as keyof EntityPermissions]
                ?.read
                ? true
                : permission.read,
              update: entityPermissions[
                permissionKey as keyof EntityPermissions
              ]?.update
                ? true
                : permission.update,
            };
          }
        }
      });
    });
  return entityPermissions;
};

// handle all the tenant cookie and theme side effects
const updateTenantBrandCookies = (tenant: Tenant) => {
  const branding = tenant?.data?.branding;
  const companyPath = tenant?.data?.companyPath;
  if (companyPath) {
    setCookieValue("companyPath", companyPath);
  } else {
    deleteCookie("companyPath");
  }
  if (branding?.primary) {
    setCookieValue("primary", branding.primary);
  } else {
    deleteCookie("primary");
  }
  if (branding?.secondary) {
    setCookieValue("secondary", branding.secondary);
  } else {
    deleteCookie("secondary");
  }
  if (branding?.logoUrl) {
    setCookieValue("logoUrl", branding.logoUrl);
  } else {
    deleteCookie("logoUrl");
  }
};

const getAccountFeatureSwitches = (
  tenant: Tenant,
): FeatureSwitches => {
  const fs: FeatureSwitches = {
    permitService: tenant?.tenantGroup?.data?.permitsEnabled || false,
    allowJobCancellation: tenant?.systemData?.ppiFeatureFlags?.allowJobCancellation || false,
  };
  return fs;
};

export const AppContext = createContext({
  state: getInitialState(),
  dispatch: (action: Action) => {
    // do nothing - this is only for the purpose of the initial value
  },
});

const AppContextProvider = ({ children }: Children): ReactNode => {
  const reducer = useCallback((state: AppState, action: Action) => {
    let newState: AppState;
    switch (action.type) {
      case ACTION_TYPE.SET_USER:
        newState = {
          ...state,
          user: action.payload,
        };
        break;
      case ACTION_TYPE.SET_HEADER_TEXT:
        newState = {
          ...state,
          headerText: capitalize(action.payload),
        };
        break;
      case ACTION_TYPE.SET_ENTITY_NAME:
        newState = {
          ...state,
          entityName: action.payload,
        };
        break;
      case ACTION_TYPE.SET_ACTIVE_TENANT:{
        const fs = getAccountFeatureSwitches(action.payload);
        newState = {
          ...state,
          tenant: action.payload,
          features: fs,
          routes: getAuthorizedRoutesForUser(
            state.user,
            action.payload.id,
            fs,
          ),
          entityPermissions: getEntityPermissions(
            state.user,
            action.payload.id,
          ),
        };
        updateTenantBrandCookies(action.payload);
        break;
      }
      case ACTION_TYPE.SET_USER_TENANTS:
        newState = {
          ...state,
          userTenants: action.payload,
        };
        break;
      case ACTION_TYPE.SET_PHONE_TYPES:
        newState = {
          ...state,
          phoneTypes: action.payload,
        };
        break;
      case ACTION_TYPE.SET_STANDARD_VIOLATIONS:
        newState = {
          ...state,
          standardViolations: action.payload.map((v) => {
            return {
              ...v,
              standard: true,
            };
          }),
        };
        break;
      case ACTION_TYPE.ADD_USER_TENANT:
        newState = {
          ...state,
          userTenants: [...state.userTenants, action.payload],
        };
        break;
      case ACTION_TYPE.SET_COLORS:
        newState = {
          ...state,
          colors: action.payload,
        };
        break;
      case ACTION_TYPE.SET_ENTITY_PERMISSIONS:
        newState = {
          ...state,
          entityPermissions: action.payload,
        };
        break;
      case ACTION_TYPE.UPDATE_CURRENT_TENANT_GROUP:
        newState = {
          ...state,
          tenant: {
            ...state.tenant,
            tenantGroup: action.payload,
          },
        };
        break;
      case ACTION_TYPE.LOGOUT:
        AuthService.LogOut();
        newState = defaultState;
        break;
      case ACTION_TYPE.SET_SIGNATURE:
        newState = {
          ...state,
          signature: action.payload,
        };
        break;
      case ACTION_TYPE.SET_FEATURES:
        newState = {
          ...state,
          features: action.payload,
        };
        break;
      default:
        newState = state;
    }
    // console.info('action:', action);
    // console.info('prevState:', state, 'newState:', newState);
    sessionStorage.setItem("state", JSON.stringify(newState));
    return newState;
  }, []);

  const [state, dispatch] = useReducer(reducer, getInitialState());
  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
